import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useEffect, useState } from "react";
import { httpClient } from "../services/http/httpClient";
import { createSort, getFieldMetaLocalStorage, setTableConfigLocalStorage, convertTableData, tableDebug, structureFieldMeta, getTableConfigLocalStorage, } from "./utils";
import Table from "./Table";
import { Placeholder, TsDataSource } from "../index";
import { useEffectUpdate } from "../hooks/useEffectUpdate";
import { generateFilter, filterHasUpdated, resetFiltersBelow, } from "../filtering/utils";
import RowCounter from "./RowCounter";
import ActionCheckModal from "./actions/ActionCheckModal";
import { ACTION_ENDPOINTS, ApiMethods } from "../constants";
import ActionModal from "./actions/ActionModal";
import { addRemoteActions } from "./actions/utils";
import { useStateFallback } from "../hooks";
function RemoteTable(props) {
    const { id, endpoint, baseUrl, source, fields, basic, forceUpdate, zone, setZone, onPageSizeChange, onToggleFilterVisibility, defaultSort, displaySource, noFilter, noPagination, noSorting, noConfigModal, noDownload, rowSelection, actions, configButtons, debug, } = props;
    const ds = new TsDataSource({ baseUrl });
    const height = props.height !== undefined ? props.height : "100%";
    // data and field information
    const [data, setData] = useState([]);
    const [fieldMeta, setFieldMeta] = useState(props.fieldMeta);
    // pagination
    const getPageSize = () => {
        if (props.pageSize)
            return props.pageSize; // if overidden with prop, ignore saved value in storage
        const size = getTableConfigLocalStorage(id, "pageSize");
        return size ?? 50;
    };
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(getPageSize);
    const [totalSize, setTotalSize] = useState(0);
    // filtering/sorting
    const [filter, setFilter] = useState({});
    const [sortColumn, setSortColumn] = useState("");
    const [sortType, setSortType] = useState("asc");
    // filter visibility
    const getFilterVisibility = () => {
        if (props.filterVisibility !== undefined)
            return props.filterVisibility; // if overidden with prop, ignore saved value in storage
        const visible = getTableConfigLocalStorage(id, "filterVisibility");
        return visible ?? true;
    };
    const [filterVisibility, setFilterVisibility] = useState(getFilterVisibility);
    // loading, error and warning info
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [error, setError] = useState("");
    // row selection
    const [selectedRows, setSelectedRows] = useStateFallback(props.selectedRows, props.setSelectedRows, []);
    const [idExportModalOpen, setIdExportModalOpen] = useState(false);
    const [idsWithReqNotMet, setIdsWithReqNotMet] = useState({});
    const [currentActionName, setCurrentActionName] = useState("");
    // action modal
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const handleSortColumn = (sortColumn, sortType) => {
        setSortColumn(sortColumn);
        setSortType(sortType);
    };
    useEffect(() => {
        const compoundedFilter = generateFilter(zone, id);
        // will trigger [filter] useEffect if update has occured
        filterHasUpdated(setFilter, filter, compoundedFilter);
        // resetFiltersBelow({id: id, zone: zone!}); occurs in <Filter />: onFilter()
    }, [zone]);
    useEffectUpdate(() => {
        renderTable();
    }, [page, sortColumn, sortType, filter, forceUpdate]);
    useEffectUpdate(() => {
        if (page === 1)
            renderTable();
        // setting page then triggers renderTable in useEffect above
        setPage(1);
        if (onPageSizeChange) {
            onPageSizeChange(pageSize);
        }
        else {
            setTableConfigLocalStorage(id, "pageSize", pageSize);
        }
    }, [pageSize]);
    useEffectUpdate(() => {
        if (onToggleFilterVisibility) {
            onToggleFilterVisibility(filterVisibility);
        }
        else {
            setTableConfigLocalStorage(id, "filterVisibility", filterVisibility);
        }
    }, [filterVisibility]);
    const onModalSave = (fm) => {
        setFieldMeta(fm);
        resetFiltersBelow({
            id: id,
            zone: zone,
            indexOffset: -1,
        });
        setZone({ ...zone });
        if (props.onModalSave) {
            props.onModalSave(fm);
        }
        else {
            setTableConfigLocalStorage(id, "fieldMeta", fm);
        }
    };
    const renderTable = () => {
        setLoading(true);
        // generating query params
        const params = {
            page: page,
            page_size: pageSize,
            filter: filter,
        };
        // deal with sorting
        if (sortColumn !== "") {
            params["sort_by"] = createSort(sortColumn, sortType);
        }
        else if (defaultSort !== undefined) {
            params["sort_by"] = defaultSort;
        }
        // get data and update state
        httpClient()
            .get("/" + endpoint, {
            params: params,
            baseURL: baseUrl,
        })
            .then(async (res) => {
            // error if endpoint doesn't return 200
            if (res.status !== 200)
                throw Error();
            const apiData = res.data.data;
            const apiMeta = res.data.meta;
            setTotalSize(apiMeta.total);
            setError("");
            // get attribute types and relationship links
            const entityMeta = basic !== true ? await ds.getEntityMeta() : undefined;
            let fm = fieldMeta;
            if (initialLoad) {
                fm = structureFieldMeta(endpoint, fieldMeta ?? getFieldMetaLocalStorage(id, fields), entityMeta, fields);
                if (!fieldMeta && !noConfigModal)
                    setTableConfigLocalStorage(id, "fieldMeta", fm);
                setFieldMeta(fm);
            }
            // debug logs if prop defined
            tableDebug(apiData, fm, debug);
            // setting data using fieldMeta
            setData(convertTableData(apiData, fm, baseUrl, entityMeta));
            setLoading(false);
            setInitialLoad(false);
        })
            .catch((error) => {
            setError(error.message);
            setLoading(false);
            setInitialLoad(false);
            setData([]);
            console.warn(error);
            console.warn("Please ensure the db has been restored");
            console.warn("Please ensure the 'endpoint' prop is correct and pluralised");
        });
    };
    if (error !== "") {
        return _jsx(Placeholder, { errorMessage: error, height: height });
    }
    if (initialLoad) {
        return _jsx(Placeholder, { loader: true, height: height });
    }
    const completeAction = async (actionName, ids) => {
        setLoading(true);
        await ds
            .custom(`/local/${ACTION_ENDPOINTS.RUN_ACTION}`, ApiMethods.POST, {
            ids: ids,
            action_name: actionName,
            object_type: endpoint,
        })
            .finally(() => {
            setActionModalOpen(true);
            setSelectedRows([]);
            setLoading(false);
        });
    };
    const convertedActions = addRemoteActions(endpoint, setCurrentActionName, setIdExportModalOpen, setIdsWithReqNotMet, setLoading, idsWithReqNotMet, completeAction, actions, baseUrl ?? undefined);
    return (_jsxs("div", { style: { height: height }, children: [_jsx(ActionCheckModal, { showIdExportModal: idExportModalOpen, setShowIdExportModal: setIdExportModalOpen, setLoading: setLoading, idsForExport: selectedRows, setIdsForExport: setSelectedRows, setIdsWithReqNotMet: setIdsWithReqNotMet, idsWithReqNotMet: idsWithReqNotMet, completeAction: completeAction, currentActionName: currentActionName }), _jsx(ActionModal, { objectType: endpoint, open: actionModalOpen, setOpen: setActionModalOpen }), _jsx(Table, { id: id, data: data, fieldMeta: fieldMeta, height: height, loading: loading, endpoint: endpoint, baseUrl: baseUrl, source: source, page: page, setPage: setPage, pageSize: pageSize, setPageSize: setPageSize, totalSize: totalSize, rowCounter: _jsx(RowCounter, { totalSize: totalSize, filter: filter, loading: loading, ...props }), displaySource: displaySource, filterVisibility: filterVisibility, setFilterVisibility: setFilterVisibility, sortColumn: sortColumn, sortType: sortType, defaultSort: defaultSort, handleSortColumn: handleSortColumn, zone: zone, setZone: setZone, filter: filter, onModalSave: onModalSave, noFilter: noFilter, noPagination: noPagination, noSorting: noSorting, noConfigModal: noConfigModal, noDownload: noDownload, rowSelection: rowSelection, selectedRows: selectedRows, setSelectedRows: setSelectedRows, actions: convertedActions, actionsFooter: {
                    name: "View Actions",
                    action: () => setActionModalOpen(true),
                }, configButtons: configButtons })] }));
}
export default RemoteTable;
